/**
 * Turning a settings object into something a human can read — the mock's `lib/settingsView.ts`
 * (sap-mockup/lib/settingsView.ts), ported.
 *
 * Used by the deploy Overview to offer the full picture instead of a summary, and to say exactly
 * which settings this rollout bent away from its template.
 *
 * Deliberately generic: it walks whatever object it is handed rather than carrying a hand-written
 * list of fields, so a setting added to a template shows up here without anyone remembering to
 * register it. That property is the whole reason to port it rather than write a per-field review —
 * a review that has to be updated by hand is a review that silently stops covering new fields.
 *
 * Labels and values default to the editors' own words (settingValues.ts); a setting missing from
 * those tables falls back to a label built from its key and its stored value.
 */
import { formatSettingValue, SETTING_GROUP_LABELS, SETTING_LABELS } from './settingValues'

export interface Leaf {
  /** Dot path, e.g. `defer.count` — the identity used when diffing. */
  path: string
  /** Human label, e.g. "Defer › Count". */
  label: string
  /** Formatted for display — also the identity used when diffing. */
  value: string
  /** Section key — the first path segment, or `__general` for top-level scalars. */
  group: string
}

export interface FlattenOptions {
  /** Extra top-level keys to ignore, on top of the bookkeeping skipped anyway. */
  skipTop?: Iterable<string>
  /** Per-path renderers for values a generic formatter cannot describe well. */
  describe?: Record<string, (value: unknown) => string>
  /** Per-path label overrides, for when the field name is not what it shows. */
  labels?: Record<string, string>
}

/**
 * Template bookkeeping — true of the template, says nothing about the deployment.
 *
 * Carries both spellings: the mock's objects are camelCase throughout, ours arrive from a Python
 * backend, so `is_default` and `schema_version` reach the same walker. A key that only ever
 * appears in one of the two costs nothing to skip in both.
 */
const DEFAULT_SKIP_TOP = new Set([
  'id',
  'name',
  'description',
  'createdAt',
  'created_at',
  'updatedAt',
  'updated_at',
  'isDefault',
  'is_default',
  'builtInDefault',
  'built_in_default',
  'usedBy',
  'used_by',
  'scope',
  'readOnly',
  'read_only',
  'enabled',
  'schemaVersion',
  'schema_version',
])

/** Loose scalars at the root have no section of their own; they share one. */
const GENERAL = '__general'

function humanize(seg: string): string {
  return seg
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
}

/** Every leaf setting in the object, in declaration order. */
export function flattenSettings(obj: unknown, opts: FlattenOptions = {}): Leaf[] {
  const skipTop = new Set([...DEFAULT_SKIP_TOP, ...(opts.skipTop ?? [])])
  const describe = opts.describe ?? {}
  const out: Leaf[] = []

  const walk = (node: unknown, path: string[], depth: number) => {
    if (!node || typeof node !== 'object') return
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      if (depth === 0 && skipTop.has(key)) continue
      const next = [...path, key]
      const dotted = next.join('.')
      const custom = describe[dotted]

      // A nested object recurses — unless a describer claims the whole subtree.
      if (!custom && value && typeof value === 'object' && !Array.isArray(value)) {
        walk(value, next, depth + 1)
        continue
      }

      out.push({
        path: dotted,
        label: opts.labels?.[dotted] ?? SETTING_LABELS[dotted] ?? next.map(humanize).join(' › '),
        value: custom ? custom(value) : formatSettingValue(dotted, value),
        group: next.length > 1 ? next[0] : GENERAL,
      })
    }
  }

  walk(obj, [], 0)
  return out
}

/** Leaves bucketed by their top-level section, ready to render. */
export function groupSettings(leaves: Leaf[]): { group: string; label: string; leaves: Leaf[] }[] {
  const order: string[] = []
  const byGroup = new Map<string, Leaf[]>()
  for (const leaf of leaves) {
    if (!byGroup.has(leaf.group)) {
      byGroup.set(leaf.group, [])
      order.push(leaf.group)
    }
    byGroup.get(leaf.group)!.push(leaf)
  }
  // General first — it is the "what is this" of the object.
  order.sort((a, b) => (a === GENERAL ? -1 : b === GENERAL ? 1 : 0))

  return order.map((group) => ({
    group,
    label: group === GENERAL ? 'General' : SETTING_GROUP_LABELS[group] ?? humanize(group),
    // The group name already heads the section; drop it from each row's label.
    leaves: byGroup.get(group)!.map((l) => ({
      ...l,
      label: l.label.split(' › ').slice(1).join(' › ') || l.label,
    })),
  }))
}

export interface FieldChange {
  path: string
  label: string
  from: string
  to: string
}

/**
 * What `next` changed relative to `base`, as a reader would see it.
 *
 * The comparison is on the DISPLAYED value, not the underlying JSON (spec §7a.1). Two assignment
 * arrays that both read "Available: All Users" have not changed in any sense the reviewer can act
 * on, and a row saying so is pure noise — so only differences someone can actually see are
 * reported.
 */
export interface DiffOptions extends FlattenOptions {
  /** Also report a setting `base` has and `next` lacks, as changing to "—". Off by default: an
   *  Adjust result that omits a key the template carries is not an edit. */
  includeRemoved?: boolean
}

export function diffSettings(
  base: unknown,
  next: unknown,
  opts: DiffOptions = {},
): FieldChange[] {
  const before = new Map(flattenSettings(base, opts).map((l) => [l.path, l]))
  const changes: FieldChange[] = []
  const seen = new Set<string>()
  for (const leaf of flattenSettings(next, opts)) {
    seen.add(leaf.path)
    const prev = before.get(leaf.path)
    if (prev && prev.value === leaf.value) continue
    // A setting that arrived from nowhere and reads as empty is not a change.
    if (!prev && leaf.value === '—') continue
    changes.push({ path: leaf.path, label: leaf.label, from: prev?.value ?? '—', to: leaf.value })
  }
  if (opts.includeRemoved) {
    for (const prev of before.values()) {
      if (seen.has(prev.path) || prev.value === '—') continue
      changes.push({ path: prev.path, label: prev.label, from: prev.value, to: '—' })
    }
  }
  return changes
}

/**
 * `diffSettings`, plus every setting whose stored value changed while its displayed value did not.
 *
 * A describer can render two different values the same way: two naming patterns that fill in to
 * the same name for this app (one with `-{DeploymentName}`, one where the name is added anyway).
 * The value was still adjusted, so it is listed as written, under `rawLabels` where given.
 */
export function diffSettingsComplete(
  base: unknown,
  next: unknown,
  opts: DiffOptions = {},
  rawLabels: Record<string, string> = {},
): FieldChange[] {
  const shown = diffSettings(base, next, opts)
  const listed = new Set(shown.map((c) => c.path))
  const raw = diffSettings(base, next, {
    skipTop: opts.skipTop,
    labels: { ...(opts.labels ?? {}), ...rawLabels },
    includeRemoved: opts.includeRemoved,
  })
  return [...shown, ...raw.filter((c) => !listed.has(c.path))]
}
